import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Label } from "semantic-ui-react";

import { PinTypeIcon, TextareaEditable, getPinColor, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import {
  QuestionResultActionSelector,
  QuestionResultMedia,
  QuestionResultStatusSelector,
  SolutionElement
} from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components";
import { useQuestionResultRemarkMutation } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/QuestionResult/hooks";
import { useAppointmentLocation, useCarBrandsQuery } from "modules/AppointmentDetails/hooks";
import { isAppointmentCanceled, isCarReady } from "utils/appointmentUtils";

import type { PinModelWithHistory } from "components";
import type { Appointment, Checklist } from "models";
import type { SortedQuestionResult } from "modules/AppointmentDetails/hooks";

type QuestionResultProps = {
  questionResult: SortedQuestionResult;
  checklist?: Checklist;
  appointment: Appointment;
  onOpenPinModal?: (pinWithHistory: PinModelWithHistory) => void;
  connectedQuestionResultId: number | null;
};

export const QuestionResult = ({ questionResult, checklist, appointment, onOpenPinModal, connectedQuestionResultId }: QuestionResultProps) => {
  const { t } = useTranslation();

  const [showMedia, setShowMedia] = useState(false);

  const appointmentLocation = useAppointmentLocation();
  const { data: brands = [] } = useCarBrandsQuery();
  const questionResultRemarkMutation = useQuestionResultRemarkMutation();

  const canUpdateQuestionResults = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.CarCheckQuestionResults);
  const canUpdateQuestionResultRemarks = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.CarCheckQuestionResultRemark);

  if (connectedQuestionResultId && questionResult.id !== connectedQuestionResultId) return null;

  const latestPin = questionResult.pin_history.at(0);
  const disabledAfterCarReady = !appointmentLocation?.can_edit_checklist_after_car_ready && isCarReady(appointment.appointment_status_identifier);
  const isDisabled = !canUpdateQuestionResults || disabledAfterCarReady || isAppointmentCanceled(appointment.appointment_status_identifier);
  const mediaCount = (questionResult.images?.filter(image => image.active).length ?? 0) + (questionResult.videos?.filter(video => video.active).length ?? 0);

  useEffect(() => {
    if (mediaCount <= 0) setShowMedia(false);
  }, [mediaCount]);

  const toggleShowMedia = () => setShowMedia(prev => !prev);
  const handleUpdateNote = (remark: string) => questionResultRemarkMutation.mutate({ question_result_id: questionResult.id, remark });
  const handlePinClick = () => {
    if (latestPin) onOpenPinModal?.({ ...latestPin, appointment, history: questionResult.pin_history });
  };

  return (
    <div className="QuestionResult">
      <div className="title-container">
        <p>{questionResult.title}</p>
        <div className="title-row">
          <p>
            <strong>{t("v8_remarks", "Remarks")}:</strong>
          </p>
          <TextareaEditable
            value={questionResult.raw}
            disabled={!canUpdateQuestionResultRemarks || disabledAfterCarReady || isAppointmentCanceled(appointment.appointment_status_identifier)}
            onSubmit={handleUpdateNote}
          />
        </div>
        <div className="QuestionResult-actions">
          {latestPin && appointmentLocation && (
            <div className="pin-status-container">
              <div className="QuestionResult-item-status">
                <Label color={getPinColor({ pin: latestPin, vin: appointment.vin_nr, location: appointmentLocation, brands, questionResult })} onClick={handlePinClick}>
                  <PinTypeIcon pinTypeId={latestPin.pin_type_id} />
                </Label>
              </div>
            </div>
          )}
          {(questionResult.customer_approved || questionResult.mechanic_fixed) && (
            <div className="status-container">
              {questionResult.customer_approved && (
                <div className="QuestionResult-item-status">
                  <Icon className="thumbs up green" />
                </div>
              )}
              {questionResult.mechanic_fixed && (
                <div className="QuestionResult-item-status">
                  <Icon className="check green" />
                </div>
              )}
            </div>
          )}
          <SolutionElement questionResult={questionResult} />
          <div className="status-container">
            <QuestionResultStatusSelector questionResult={questionResult} disabled={isDisabled} />
            {mediaCount > 0 && (
              <div onClick={toggleShowMedia}>
                <Icon className="images grey pointer" size="small" />
                <p>
                  <small>{mediaCount}</small>
                </p>
              </div>
            )}
            <QuestionResultActionSelector questionResult={questionResult} checklist={checklist} appointment={appointment} />
          </div>
        </div>
      </div>
      {showMedia && <QuestionResultMedia questionResult={questionResult} canUpdate={canUpdateQuestionResults} appointment={appointment} />}
    </div>
  );
};
